import React from 'react'
import { Button, CircularProgress, Box, Typography, Tooltip, styled } from '@mui/material'

/**
 * Botón estilizado con Material-UI usando el sistema styled.
 * @private
 */
const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'labelM' && prop !== 'version',
})(({ labelM, version }) => ({
  margin: '20px 0',
  borderRadius: 6,
  height: 48,

  ...(version === 'delete' && {
    backgroundColor: '#dd422a',
    '&:hover': {
      backgroundColor: '#c0392b',
    },
  }),

  textTransform: 'none',
  fontFamily: 'FSEmeric-Medium',
  fontSize: 14,

  ...((labelM === 'large' && { padding: '6px 65px' }) ||
    (labelM === 'medium' && { padding: '6px 35px' }) ||
    {}),
}))

/**
 * @typedef {object} ThemeButtonProps
 * @property {'text' | 'outlined' | 'contained'} [variant='contained'] - La variante visual del botón.
 * @property {'primary' | 'secondary' | 'default' | 'error' | 'warning' | 'info' | 'success'} [color='primary'] - El color del botón.
 * @property {'small' | 'medium' | 'large'} [labelM='small'] - El tamaño del padding del botón.
 * @property {React.ReactNode} children - El contenido del botón (texto, iconos, etc.).
 * @property {boolean} [loading=false] - Si es `true`, muestra un spinner de carga en lugar del contenido.
 * @property {object} [sx={}] - Estilos adicionales usando el sistema sx de Material-UI.
 * @property {React.ReactNode} [icon] - Icono que se muestra junto al texto del botón.
 * @property {string} [tooltip] - Texto del tooltip que se muestra al pasar el mouse sobre el botón.
 * @property {string} [extra] - Texto adicional que se muestra debajo del botón.
 * @property {'delete'} [version] - Versión especial del botón con estilos predefinidos (ej. 'delete' para botones de eliminación).
 * @property {boolean} [disabled] - Si es `true`, deshabilita el botón.
 */

/**
 * Componente de botón temático personalizado con estados de carga y tooltip.
 * 
 * Proporciona un botón estilizado con:
 * - **Estados de carga**: Muestra un spinner cuando está procesando
 * - **Tamaños configurables**: small, medium, large con padding personalizado
 * - **Tooltip opcional**: Información contextual al pasar el mouse
 * - **Texto extra**: Subtexto debajo del botón
 * - **Iconos**: Soporte para iconos junto al texto
 * - **Versiones especiales**: Estilos predefinidos para acciones específicas (delete)
 * - **Color default**: Soporte para color gris neutral
 * 
 * @component
 * @example
 * const MyActions = () => {
 *   const [loading, setLoading] = useState(false);
 * 
 *   const handleSave = async () => {
 *     setLoading(true);
 *     await saveData();
 *     setLoading(false);
 *   };
 * 
 *   return (
 *     <>
 *       <ThemeButton
 *         color="primary"
 *         labelM="large"
 *         loading={loading}
 *         onClick={handleSave}
 *         tooltip="Guardar cambios"
 *       >
 *         Guardar
 *       </ThemeButton>
 *       <ThemeButton
 *         version="delete"
 *         onClick={handleDelete}
 *       >
 *         Eliminar
 *       </ThemeButton>
 *     </>
 *   );
 * };
 * 
 * @param {ThemeButtonProps} props - Las props del componente ThemeButton.
 */
export const ThemeButton = ({
  variant = 'contained',
  color = 'primary',
  labelM = 'small',
  children,
  loading = false,
  sx = {},
  ...props
}) => {
  const isDefault = color === 'default'

  const ButtonComponent = (
    <StyledButton
      {...props}
      variant={variant}
      color={color}
      labelM={labelM}
      disabled={props.disabled || loading}
      disableElevation
      sx={{
        ...(isDefault && {
          bgcolor: '#e0e0e0',
          '&:hover': {
            bgcolor: '#d5d5d5',
          },
        }),
        ...sx,
      }}
    >
      {loading ? (
        <CircularProgress size={30} color={color} />
      ) : (
        <Box display="flex" alignItems="center" component="span">
          {props.icon}
          {children}
        </Box>
      )}
    </StyledButton>
  )

  return (
    <Box>
      {props.tooltip ? (
        <Tooltip title={props.tooltip} arrow placement="top">
          {ButtonComponent}
        </Tooltip>
      ) : (
        ButtonComponent
      )}
      {props.extra && (
        <Box mt={-2.5} sx={sx}>
          <Typography variant="caption" color="textSecondary">
            {props.extra}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
